import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { AreaApiService } from '../area/area.service';
import {
  DictionaryItem,
  DictionaryService,
} from 'src/app/services/dictionary.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  NotesList,
  NotesListPageChanged,
  SelectOption,
  SnackbarService,
} from '@bcl/design-system';
import { combineLatest, Subject, takeUntil, Subscription } from 'rxjs';
import {
  LocationDetailsOptions,
  Monitor,
  MonitorWithOptions,
} from '../area/area.model';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  MonitorEditDialogComponent,
  EditMonitorDialogData,
  EditMonitorResult,
} from './components/monitor-edit-dialog/monitor-edit-dialog.component';
import {
  AddProductDialogData,
  AddProductService,
} from './components/consumables-dialog/consumables-dialog.service';
import { ObservationDetails } from './components/area-monitor-status/area-monitor-status.component';
import { ParentObject } from 'src/app/shared-components/components/notes-list/notes.model';
import { NotesApiService } from 'src/app/shared-components/components/notes-list/notes-api.service';
import { ProductEmitEvent } from './components/area-monitor-products/area-monitor-products.component';
import { StateService } from 'src/app/services/state.service';
import { StorageService } from 'src/app/services/storage.service';
import { Platform } from '@ionic/angular';
import { DataProviderService } from 'src/app/services/data-provider.service';
import { VisitService } from 'src/app/services/visit.service';
import { BackButtonService } from 'src/app/services/back-button.service';

@Component({
  selector: 'app-area-monitor',
  templateUrl: './area-monitor.component.html',
  styleUrls: ['./area-monitor.component.scss'],
})
export class AreaMonitorPage implements OnInit, OnDestroy {
  public loading = true;
  public hasError = false;
  public visitId: number;
  public date: string;
  public dayNumber: string;
  public areaId: number;
  public monitorId: number;
  public routerState: any;
  public parentObject: ParentObject = 'PC.Monitor';
  public parentId = 0;
  public notesList: NotesList = { notes: [], totalEntries: 0 };
  public dateFilterOptions: SelectOption[] = [];
  public selectedMonitor?: MonitorWithOptions;
  public filteredMonitors: MonitorWithOptions[] = [];
  public currentMonitorIndex = -1;
  public hasNextMonitor = false;
  public visit?: any;
  public options: LocationDetailsOptions = {
    location: {
      technicians: [],
      skills: [],
      evaluationQuestions: [],
      days: [],
    },
    area: { types: [], subtypes: [], certifications: [] },
    monitor: {
      modules: [],
      types: [],
      statuses: [],
      product: { products: [], units: [], warehouses: [] },
      groups: [],
    },
  };
  public observations: SelectOption[] = [];
  public defaultCar: number | null = null;
  public isEditingDisabled = false;
  public isProcessing = false;
  private lastDateFilter = '0';
  private destroyed$ = new Subject<void>();
  private backButtonSubscription: Subscription;
  private allVisitsForDay: any[] = [];

  constructor(
    private areaApiService: AreaApiService,
    private dictionaryService: DictionaryService,
    private route: ActivatedRoute,
    private snackbarService: SnackbarService,
    private router: Router,
    public dialog: MatDialog,
    private addProductService: AddProductService,
    private notesApi: NotesApiService,
    private stateService: StateService,
    private storageService: StorageService,
    private platform: Platform,
    private dataService: DataProviderService,
    private visitService: VisitService,
    private backButtonService: BackButtonService,
    private cdr: ChangeDetectorRef
  ) {}

  ionViewWillEnter(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });
  }

  ngOnInit(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });

    this.backButtonSubscription =
      this.platform.backButton.subscribeWithPriority(10, () => {
        this.dialog.closeAll();
        this.goBack();
      });
    this.route.params.subscribe((params) => {
      this.visitId = params?.id;
      this.date = params?.date;
      this.dayNumber = params?.dayId;
      this.areaId = params?.areaid;
      this.monitorId = params?.monitorId;
      this.parentId = this.monitorId;
      this.routerState = this.router.getCurrentNavigation().extras.state;

      this.loadSelectedCarForToday();

      if (this.routerState?.filteredMonitors) {
        this.filteredMonitors = this.routerState.filteredMonitors;
        this.currentMonitorIndex = this.filteredMonitors.findIndex(
          (m) => m.id.toString() === this.monitorId.toString()
        );
        const nextMonitor = this.filteredMonitors.find(
          (m) => m.id.toString() === this.monitorId.toString()
        );
        this.hasNextMonitor =
          this.currentMonitorIndex < this.filteredMonitors.length - 1;
      }

      combineLatest({
        monitor: this.areaApiService.getMonitor(this.monitorId, this.visitId),
        options: this.areaApiService.getLocationDetailsOptions(this.areaId),
        groups:
          this.dictionaryService.getDictionaryItemsByKey('PC.INSTRUMENT.TYPE'),
        observations: this.dictionaryService.getDictionaryItemsByKey(
          'PC.OBSERVATION.TYPE'
        ),
        dateFilters: this.dictionaryService.getDateFilters(),
        visit: this.dataService.getEljakimJob(this.visitId.toString(), true),
        allVisitsForDay: this.dataService.getEljakimJobs(this.date),
      })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: ({
            monitor,
            options,
            groups,
            observations,
            dateFilters,
            visit,
            allVisitsForDay,
          }) => {
            // Validate monitor data
            if (!monitor) {
              console.error('Monitor data not found');
              this.handleDataLoadError();
              return;
            }

            this.options = {
              ...options,
              monitor: {
                ...options.monitor,
                groups,
              },
            };
            this.selectedMonitor = this.mapMonitor(monitor);
            this.observations = observations.map((o) => ({
              label: o.name,
              value: o.value,
            }));
            this.dateFilterOptions = dateFilters.map((x) => ({
              value: x.value,
              label: x.name,
            }));
            this.visit = visit;
            this.allVisitsForDay = allVisitsForDay;
            this.updateDisabledStates();
            this.loading = false;
            this.hasError = false;
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Error loading monitor data:', error);
            this.handleDataLoadError();
          },
        });
    });
  }

  public monitorStatusChange(status: boolean): void {
    this.isProcessing = true;

    const updatedDetails = {
      ...this.selectedMonitor,
      statusId: status ? '1' : '2',
    };

    this.areaApiService.updateMonitorDetails(updatedDetails).subscribe({
      next: () => {
        this.snackbarService.open(
          'green',
          'MONITOR.DETAILS.MESSAGES.MONITOR_DETAILS_UPDATED'
        );
        this.refreshMonitor();
      },
      error: () => {
        this.snackbarService.open(
          'red',
          'MONITOR.DETAILS.MESSAGES.MONITOR_DETAILS_UPDATE_ERROR'
        );
        this.isProcessing = false;
      },
    });
  }

  public observationChange(observationDetails: ObservationDetails): void {
    if (observationDetails?.id) {
      this.isProcessing = true;

      this.areaApiService
        .changeMonitorObservation(
          this.monitorId,
          this.visitId,
          observationDetails
        )
        .subscribe({
          next: () => {
            this.snackbarService.open(
              'green',
              'MONITOR.DETAILS.MESSAGES.OBSERVATION_CHANGED'
            );

            this.refreshMonitor();
          },
          error: () => {
            this.snackbarService.open(
              'red',
              'MONITOR.DETAILS.MESSAGES.OBSERVATION_CHANGE_ERROR'
            );
            this.isProcessing = false;
          },
        });
    }
  }

  public editMonitor(): void {
    if (!this.selectedMonitor) {
      return;
    }

    const monitor = this.selectedMonitor;
    const currentMonitor = {
      moduleName:
        this.options.monitor.modules.find((m) => m.value === monitor.moduleId)
          ?.label || '',
      typeName:
        this.options.monitor.types.find((t) => t.value === monitor.typeId)
          ?.label || '',
      ...monitor,
    };
    this.openEditMonitorDialog({
      data: {
        currentMonitor,
        ...this.options.monitor,
      },
    })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          const updatedDetails = {
            ...this.selectedMonitor,
            ...result,
          };
          this.isProcessing = true;

          this.areaApiService.updateMonitorDetails(updatedDetails).subscribe({
            next: () => {
              this.snackbarService.open(
                'green',
                'MONITOR.DETAILS.MESSAGES.MONITOR_DETAILS_UPDATED'
              );
              this.refreshMonitor();
            },
            error: () => {
              this.snackbarService.open(
                'red',
                'MONITOR.DETAILS.MESSAGES.MONITOR_DETAILS_UPDATE_ERROR'
              );
              this.isProcessing = false;
            },
          });
        }
      });
  }

  public monitorReplaced(newMonitorId?: number): void {
    if (!newMonitorId) {
      this.refreshMonitor();
      return;
    }

    this.filteredMonitors = this.filteredMonitors.filter(
      (m) => m.id !== this.monitorId
    );

    if (this.currentMonitorIndex >= this.filteredMonitors.length) {
      this.currentMonitorIndex = this.filteredMonitors.length - 1;
    }

    this.router.navigate(
      [
        '/area/monitor',
        this.date,
        this.visitId,
        this.dayNumber,
        this.areaId,
        newMonitorId,
      ],
      {
        state: {
          backUrl:
            this.routerState?.backUrl ||
            `/area/${this.date}/${this.visitId}/${this.dayNumber}/${this.areaId}`,
          filteredMonitors: this.filteredMonitors,
        },
      }
    );
  }

  public addProduct(): void {
    const dialogData: AddProductDialogData = {
      ...this.options.monitor.product,
      operationType: 'add',
      defaultCar: this.defaultCar,
      sameMonitorsCount: this.selectedMonitor?.sameMonitorsCount,
      moduleId: this.selectedMonitor?.moduleId,
      monitorId: this.monitorId,
      newMonitorId: this.selectedMonitor?.newId,
      visitId: this.visitId,
      modules: this.options.monitor.modules,
    };
    this.addProductService
      .open({ data: dialogData })
      .afterClosed()
      .subscribe((product) => {
        product.initialDate = new Date();
        const updatedDetails = {
          ...product,
          monitorId: this.monitorId,
          externalId: this.visitId,
        };
        this.isProcessing = true;

        this.addProductService.addMonitorProduct(updatedDetails).subscribe({
          next: () => {
            this.snackbarService.open(
              'green',
              'MONITOR.DETAILS.MESSAGES.PRODUCT_ADDED'
            );
            this.refreshMonitor();
          },
          error: () => {
            this.snackbarService.open(
              'red',
              'MONITOR.DETAILS.MESSAGES.PRODUCT_ADD_ERROR'
            );
            this.isProcessing = false;
          },
        });
      });
  }

  public editProduct(event: ProductEmitEvent): void {
    const dialogData: AddProductDialogData = {
      ...this.options.monitor.product,
      product: event.product,
      operationType: event.eventType,
      defaultCar: this.defaultCar,
      sameMonitorsCount: this.selectedMonitor?.sameMonitorsCount,
      moduleId: this.selectedMonitor?.moduleId,
      monitorId: this.monitorId,
      newMonitorId: this.selectedMonitor?.newId,
      visitId: this.visitId,
      modules: this.options.monitor.modules,
    };
    this.addProductService
      .open({ data: dialogData })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          if (
            result.action === 'storeInWarehouse' ||
            result.action === 'replace'
          ) {
            const updatedDetails = {
              ...result,
              monitorId: this.monitorId,
              externalId: this.visitId,
              initialDate: new Date(),
            };
            this.isProcessing = true;

            this.addProductService
              .editMonitorProduct(updatedDetails)
              .subscribe({
                next: () => {
                  this.snackbarService.open(
                    'green',
                    'MONITOR.DETAILS.MESSAGES.PRODUCT_UPDATED'
                  );
                  this.refreshMonitor();
                },
                error: () => {
                  this.snackbarService.open(
                    'red',
                    'MONITOR.DETAILS.MESSAGES.PRODUCT_UPDATE_ERROR'
                  );
                  this.isProcessing = false;
                },
              });
          } else if (result.action === 'delete') {
            this.isProcessing = true;

            if (result.executeAll) {
              this.addProductService
                .deleteMonitorProductAll(result.monitorProductId, this.visitId)
                .subscribe({
                  next: () => {
                    this.snackbarService.open(
                      'green',
                      'MONITOR.DETAILS.MESSAGES.PRODUCT_DELETED'
                    );
                    this.refreshMonitor();
                  },
                  error: () => {
                    this.snackbarService.open(
                      'red',
                      'MONITOR.DETAILS.MESSAGES.PRODUCT_DELETE_ERROR'
                    );
                    this.isProcessing = false;
                  },
                });
            } else if (
              result.executeSelected &&
              result.selectedMonitors?.length > 0
            ) {
              this.addProductService
                .deleteMonitorProductSelected(
                  result.monitorProductId,
                  this.visitId,
                  result.selectedMonitors
                )
                .subscribe({
                  next: () => {
                    this.snackbarService.open(
                      'green',
                      'MONITOR.DETAILS.MESSAGES.PRODUCT_DELETED'
                    );
                    this.refreshMonitor();
                  },
                  error: () => {
                    this.snackbarService.open(
                      'red',
                      'MONITOR.DETAILS.MESSAGES.PRODUCT_DELETE_ERROR'
                    );
                    this.isProcessing = false;
                  },
                });
            } else {
              this.addProductService
                .deleteMonitorProduct(result.monitorProductId)
                .subscribe({
                  next: () => {
                    this.snackbarService.open(
                      'green',
                      'MONITOR.DETAILS.MESSAGES.PRODUCT_DELETED'
                    );
                    this.refreshMonitor();
                  },
                  error: () => {
                    this.snackbarService.open(
                      'red',
                      'MONITOR.DETAILS.MESSAGES.PRODUCT_DELETE_ERROR'
                    );
                    this.isProcessing = false;
                  },
                });
            }
          }
        }
      });
  }

  public openEditMonitorDialog(
    settings: MatDialogConfig<EditMonitorDialogData>
  ): MatDialogRef<MonitorEditDialogComponent, EditMonitorResult | undefined> {
    return this.dialog.open(MonitorEditDialogComponent, {
      ...settings,
    });
  }

  public goBack(): void {
    if (this.routerState?.backUrl.includes('area/map')) {
      this.router.navigateByUrl(`${this.routerState?.backUrl}`);
    } else {
      this.router
        .navigateByUrl('/home', { skipLocationChange: true })
        .then(() => {
          this.router.navigateByUrl(`${this.routerState?.backUrl}`);
        });
    }
  }

  public updateDisabledStates(): void {
    this.isEditingDisabled = this.visitService.isEditingDisabled(this.visit);
  }

  public goToHome(): void {
    this.router.navigate(['/home']);
  }

  public loadMoreNotes(page: NotesListPageChanged): void {
    this.lastDateFilter = page.filter.date ?? '0';
    this.getNotes(
      this.lastDateFilter,
      page.pageNumber,
      page.pageSize,
      this.parentObject,
      this.parentId
    );
  }

  public getNotes(
    dateFilterString: string,
    pageNumber: number,
    pageSize: number,
    parentObject: ParentObject,
    parentId: number
  ): void {
    this.notesApi
      .getParentNotes(
        { parentID: parentId, parentObject, dateFilter: dateFilterString },
        pageNumber,
        pageSize
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (result) => {
          this.notesList = result;
        },
        error: () => {
          this.snackbarService.open(
            'red',
            'GENERIC_ERROR_MESSAGES.AN_ERROR_OCCURRED_UNABLE_TO_LOAD_DATA'
          );
        },
      });
  }

  ngOnDestroy() {
    this.backButtonSubscription?.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public goToNextMonitor(): void {
    if (this.currentMonitorIndex < this.filteredMonitors.length - 1) {
      const nextMonitor = this.filteredMonitors[this.currentMonitorIndex + 1];
      this.router.navigate(
        [
          '/area/monitor',
          this.date,
          this.visitId,
          this.dayNumber,
          this.areaId,
          nextMonitor.id,
        ],
        {
          state: {
            backUrl:
              this.routerState?.backUrl ||
              `/area/${this.date}/${this.visitId}/${this.dayNumber}/${this.areaId}`,
            filteredMonitors: this.filteredMonitors,
          },
        }
      );
    }
  }
  private handleDataLoadError(): void {
    this.hasError = true;
    this.loading = false;
    this.selectedMonitor = undefined;

    this.snackbarService.open(
      'red',
      'GENERIC_ERROR_MESSAGES.AN_ERROR_OCCURRED_UNABLE_TO_LOAD_DATA'
    );
  }
  private refreshMonitor(): void {
    this.areaApiService.getMonitor(this.monitorId, this.visitId).subscribe({
      next: (monitor) => {
        this.selectedMonitor = this.mapMonitor(monitor);
        this.isProcessing = false;
      },
      error: () => {
        this.snackbarService.open(
          'red',
          'GENERIC_ERROR_MESSAGES.AN_ERROR_OCCURRED_UNABLE_TO_LOAD_DATA'
        );
        this.isProcessing = false;
      },
    });
  }

  private mapMonitor(monitor: Monitor): MonitorWithOptions {
    const status = this.options.monitor.statuses.find(
      (s) => s.value === monitor.statusId
    );

    return {
      ...monitor,
      moduleName:
        this.options.monitor.modules.find((m) => m.value === monitor.moduleId)
          ?.label ?? '',
      typeName:
        this.options.monitor.types.find((t) => t.value === monitor.typeId)
          ?.label ?? '',
      typeToken: this.options.monitor.types.find(
        (t) => t.value === monitor.typeId
      )?.token,
      status: {
        name: status?.label ?? '',
        style: status?.style,
        statusId: monitor.statusId,
      },
      batteryStatus: monitor.batteryStatus
        ? +(monitor.batteryStatus * 100).toFixed(2)
        : undefined,
    };
  }

  private async loadSelectedCarForToday(): Promise<void> {
    try {
      const currentDate = new Date();
      const dateKey = currentDate.toISOString().split('T')[0];
      const storageKey = `${dateKey}_selectedCar`;

      const todaysSelectedCar = await this.storageService.getObject(storageKey);

      if (todaysSelectedCar) {
        this.defaultCar =
          typeof todaysSelectedCar === 'string'
            ? parseInt(todaysSelectedCar, 10)
            : todaysSelectedCar;
      } else {
        this.defaultCar = this.stateService.getValue('defaultCar');
      }
    } catch (error) {
      this.defaultCar = this.stateService.getValue('defaultCar');
    }
  }
}
